import { Component } from '@theme/component';

/** Progressive enhancement of the complete, server-rendered design catalog. */
class SurfaceTheoryCatalog extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.controller?.abort();
    this.controller = new AbortController();
    this.initialize().catch(() => {
      // Native product links and pagination remain usable if enhancement fails.
      this.querySelector('[data-filters]').hidden = true;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.controller?.abort();
    clearTimeout(this.searchTimer);
  }

  async initialize() {
    const { signal } = this.controller;
    this.form = this.querySelector('[data-filters]');
    this.grid = this.querySelector('[data-grid]');
    // Retrieve remaining native pages before offering whole-catalog filtering.
    let next = this.querySelector('[data-next-page]')?.href;
    const visited = new Set();
    while (next && !visited.has(next)) {
      visited.add(next);
      const response = await fetch(next, { signal });
      if (!response.ok) throw new Error('Catalog page unavailable');
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      const page = document.querySelector('st-shop-catalog');
      if (!page) throw new Error('Catalog page missing');
      const handles = new Set(Array.from(this.grid.children, card => card.dataset.handle));
      for (const card of page.querySelectorAll('[data-design]')) {
        if (!handles.has(card.dataset.handle)) this.grid.append(card);
      }
      next = page.querySelector('[data-next-page]')?.getAttribute('href');
      if (next) next = new URL(next, location.href).href;
    }
    if (signal.aborted) return;
    this.querySelector('[data-pagination]')?.setAttribute('hidden', '');
    this.colorLabels = JSON.parse(this.dataset.colorLabels || '{}');
    this.cards = Array.from(this.grid.querySelectorAll('[data-design]'));
    const featuredCount = Number(this.dataset.featuredCount || 0);
    const first = this.cards.slice(0, featuredCount);
    const buckets = new Map();
    this.cards.slice(featuredCount).forEach(card => {
      const key = card.dataset.series || card.dataset.style || '';
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(card);
    });
    // Mix families and colors after the editable featured selection.
    let rotation = 0;
    for (const [key, bucket] of buckets) {
      const offset = rotation++ % bucket.length;
      buckets.set(key, [...bucket.slice(offset), ...bucket.slice(0, offset)]);
    }
    const mixed = [];
    while (Array.from(buckets.values()).some(bucket => bucket.length)) {
      for (const bucket of buckets.values()) if (bucket.length) mixed.push(bucket.shift());
    }
    this.cards = [...first, ...mixed];
    this.items = this.cards.map((card, rank) => {
      const d = card.dataset;
      const color = this.colorFamily(d.colorFamily || d.color || d.title);
      const style = d.style === 'Contour' ? 'Minimal' : d.style;
      return { card, rank, color, style, collection: d.series, title: d.title,
        price: Number(d.price), date: Number(d.date),
        search: `${d.title} ${d.color} ${color} ${style} ${d.series}`.toLowerCase() };
    });
    this.controls = Object.fromEntries(['q', 'color', 'style', 'collection', 'sort'].map(key => [key, this.form.elements.namedItem(`st_${key}`)]));
    for (const key of ['color', 'style', 'collection']) {
      const values = [...new Set(this.items.map(item => item[key]).filter(Boolean))].sort((a, b) => a.localeCompare(b));
      for (const value of values) {
        const label = key === 'color' ? this.colorLabels[value.toLowerCase()] || value : value;
        const option = new Option(label, value);
        option.dataset.label = label;
        this.controls[key].add(option);
      }
    }
    this.readURL();
    const mobile = window.matchMedia('(max-width: 749px)');
    const panel = this.querySelector('[data-filter-panel]');
    panel.open = !mobile.matches;
    mobile.addEventListener('change', event => { panel.open = !event.matches; }, { signal });
    this.form.hidden = false;
    this.render(false);
    this.form.addEventListener('submit', event => event.preventDefault(), { signal });
    this.form.addEventListener('change', () => { this.limit = 24; this.render(); }, { signal });
    this.controls.q.addEventListener('input', () => {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => { this.limit = 24; this.render(); }, 150);
    }, { signal });
    for (const button of this.querySelectorAll('[data-clear], [data-reset]')) {
      button.addEventListener('click', () => {
        this.form.reset(); this.limit = 24; this.render(); this.controls.q.focus();
      }, { signal });
    }
    this.querySelector('[data-more]').addEventListener('click', () => {
      const previous = this.limit;
      this.limit += 24; this.render();
      this.matches[previous]?.card.querySelector('a')?.focus({ preventScroll: true });
    }, { signal });
    this.grid.addEventListener('click', event => {
      if (event.target.closest('a')) {
        try { sessionStorage.setItem(this.scrollKey(), String(window.scrollY)); } catch { /* Storage is optional. */ }
      }
    }, { signal });
    window.addEventListener('popstate', () => { this.readURL(); this.render(false); }, { signal });
    if (performance.getEntriesByType('navigation')[0]?.type === 'back_forward') {
      try {
        const scroll = sessionStorage.getItem(this.scrollKey());
        if (scroll !== null) requestAnimationFrame(() => window.scrollTo(0, Number(scroll)));
      } catch { /* Native browser restoration remains available. */ }
    }
  }

  colorFamily(label) {
    const text = label.toLowerCase();
    const families = [
      ['Purple', /amethyst|aubergine|lilac|purple/], ['Teal', /teal/],
      ['Green', /forest|green|sage|olive|celadon|emerald/],
      ['Black', /black|obsidian|monochrome/], ['Grey', /grey|gray|charcoal|graphite|slate/],
      ['Blue', /blue|navy|cobalt|midnight|arctic|artic|frosted/],
      ['Red', /burgundy|crimson|oxblood|red/], ['Pink', /rose|pink/],
      ['Orange', /terra(?:\s|$)|orange/], ['Brown', /espresso|chocolate|cognac|bronze|brown/],
      ['Neutral', /white|ivory|porcelain|beige|cream/], ['Gold', /gilded|gold/],
    ];
    return families.find(([, pattern]) => pattern.test(text))?.[0] || label;
  }

  readURL() {
    const params = new URL(location.href).searchParams;
    for (const [key, control] of Object.entries(this.controls)) {
      control.value = params.get(`st_${key}`) || (key === 'sort' ? 'featured' : '');
      if (key === 'sort' && !control.value) control.value = 'featured';
    }
    this.limit = Math.max(24, Math.min(this.items.length || 24, Number(params.get('st_limit')) || 24));
  }

  scrollKey() { return `st-catalog-scroll:${location.pathname}${location.search}`; }

  render(updateURL = true) {
    const selected = Object.fromEntries(Object.entries(this.controls).map(([key, control]) => [key, control.value]));
    const words = selected.q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const accepts = (item, except) => words.every(word => item.search.includes(word)) &&
      ['color', 'style', 'collection'].every(key => key === except || !selected[key] || item[key] === selected[key]);
    this.matches = this.items.filter(item => accepts(item));
    const comparators = {
      featured: (a, b) => a.rank - b.rank,
      newest: (a, b) => b.date - a.date || a.rank - b.rank,
      'price-asc': (a, b) => a.price - b.price || a.rank - b.rank,
      'price-desc': (a, b) => b.price - a.price || a.rank - b.rank,
      title: (a, b) => a.title.localeCompare(b.title),
    };
    this.matches.sort(comparators[selected.sort] || comparators.featured);
    for (const item of this.items) item.card.hidden = true;
    const fragment = document.createDocumentFragment();
    this.matches.forEach((item, index) => { item.card.hidden = index >= this.limit; fragment.append(item.card); });
    this.grid.append(fragment);
    for (const key of ['color', 'style', 'collection']) {
      for (const option of this.controls[key].options) {
        if (!option.value) continue;
        const count = this.items.filter(item => item[key] === option.value && accepts(item, key)).length;
        option.textContent = `${option.dataset.label} (${count})`;
        option.disabled = count === 0 && selected[key] !== option.value;
      }
    }
    const total = this.matches.length;
    this.querySelector('[data-count]').textContent = (total === 1 ? this.dataset.countOneLabel : this.dataset.countLabel).replace('{count}', String(total));
    this.querySelector('[data-progress]').textContent = this.dataset.progressLabel.replace('{shown}', String(Math.min(total, this.limit))).replace('{total}', String(total));
    this.querySelector('[data-empty]').hidden = total !== 0;
    this.querySelector('[data-more-wrap]').hidden = total === 0;
    this.querySelector('[data-more]').hidden = this.limit >= total;
    this.querySelector('[data-clear]').hidden = !selected.q && !selected.color && !selected.style && !selected.collection;
    if (updateURL) {
      const url = new URL(location.href);
      for (const [key, value] of Object.entries(selected)) {
        if (!value || (key === 'sort' && value === 'featured')) url.searchParams.delete(`st_${key}`);
        else url.searchParams.set(`st_${key}`, value);
      }
      if (this.limit > 24) url.searchParams.set('st_limit', String(this.limit));
      else url.searchParams.delete('st_limit');
      history.replaceState(history.state, '', url);
    }
  }
}

if (!customElements.get('st-shop-catalog')) customElements.define('st-shop-catalog', SurfaceTheoryCatalog);

